import { Grid, RadioGroup } from '@mui/material'
import type { SelectChangeEvent } from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import { FieldValues, useFormState } from 'react-hook-form'
import {
  AccessScheduleType,
  computeScheduleDisplayDurationMs,
  Frequency,
  expandWeekdayRange,
} from 'shared/utils/schedule'
import { scheduleFormToIcal } from 'shared/utils/vevent'
import { Environment } from 'shared/types'
import { PermanentAccessSection } from './SectionPermanentAccess'
import { SectionCyclicAccess } from './SectionCyclicAccess'
import { SectionTimeAccess } from './SectionTimeAccess'
import {
  applyScheduleDateValuesUpdate,
  DEFAULT_WEEKLY_ISO_DAYS,
  applyScheduleFrequencyChange,
  applySnapshotForScheduleTypeChange,
  buildRepeatUntilDatePickerSharedProps,
  buildTemporaryDatePickerSharedProps,
  clearScheduleStructureErrors,
  cloneScheduleSnapshotFieldValue,
  computeScheduleDayState,
  computeWeeklyRangeSelection,
  formatScheduleSetTimeLabel,
  getScheduleDaysOfWeekPickerError,
  getScheduleFieldsErrorMessage,
  listTriggerFieldsAfterDateClear,
  resetInactiveScheduleBranchesForType,
  restoreRecurringFieldsFromSnapshot,
  resolveScheduleDatetimeTimezone,
  runScheduleStructureRevalidation,
  shouldClearDailyMidnightScheduleTimes,
  shouldValidateInterdependentScheduleFields,
  SCHEDULE_ACCESS_TIME_MINUTES_STEP,
  type ScheduleDateValuesUpdateOptions,
  type ScheduleFieldsProps,
  type ScheduleTimesBeforeAllDayRef,
} from './ScheduleFields.utils'

export type {
  ScheduleFieldNames,
  ScheduleValueConfig,
  ScheduleTranslations,
  ScheduleErrorVisibility,
  ScheduleFieldsProps,
} from './ScheduleFields.utils'

export function ScheduleFields<T extends FieldValues = FieldValues>(
  props: ScheduleFieldsProps<T>
) {
  const {
    control,
    setValue,
    watch,
    clearErrors,
    trigger,
    fieldNames,
    valueConfig,
    t,
    clearLabel,
    shouldShowErrors,
    errorsVisibility,
    clearDaysOfWeekWhenLeavingDaily,
    unsupportedRecurringPattern,
    unsupportedAlertSeverity = 'error',
    disabled = false,
    datePickerUseShowTimezoneInLabel = false,
    temporaryDatesRevalidate,
    repeatUntilRevalidate,
    labels,
    defaultSetValueOptions,
    extraClearErrorsOnScheduleTypeChange = [],
    extraClearErrorsOnFrequencyChange = [],
    embeddedTimeZoneControl,
    scheduleDatetimeFieldTimeZone,
    gridItemProps = { xs: 12, lg: 6 },
    radioGroupIds = {},
    daysOfWeekErrorField = 'daysOfWeek',
    daysOfWeekSetValueOptions,
    scheduleAllDayCheckboxUsesSetValue = false,
    scheduleTimePickerMinutesStep = SCHEDULE_ACCESS_TIME_MINUTES_STEP,
    resetInactiveScheduleBranchValuesOnTypeChange = false,
    clearScheduleErrorsAfterStructureRevalidate = false,
    initialScheduleSnapshot,
  } = props

  const { errors } = useFormState({ control })
  const [scheduleFromTimeOpen, setScheduleFromTimeOpen] = useState(false)
  const [scheduleToTimeOpen, setScheduleToTimeOpen] = useState(false)
  const scheduleTimesBeforeAllDayRef =
    useRef<ScheduleTimesBeforeAllDayRef>(null)
  const daysOfWeekSnapshotSelectedRef = useRef<string[] | null>(null)
  const daysOfWeekSnapshotWeeklyRef = useRef<string[] | null>(null)
  const scheduleTimesBeforeWeekPatternClearRef =
    useRef<ScheduleTimesBeforeAllDayRef>(null)

  const scheduleType = watch(fieldNames.scheduleType as any) as string
  const frequency = watch(fieldNames.frequency as any) as string
  const daysOfWeekRaw = watch(fieldNames.daysOfWeek as any) as string[]
  const scheduleAllDay = watch(fieldNames.scheduleAllDay as any) as boolean
  const scheduleFromDate = watch(
    fieldNames.scheduleFromDate as any
  ) as Date | null
  const scheduleToDate = watch(fieldNames.scheduleToDate as any) as Date | null
  const temporaryFromDate = watch(
    fieldNames.temporaryFromDate as any
  ) as Date | null
  const temporaryToDate = watch(
    fieldNames.temporaryToDate as any
  ) as Date | null
  const repeatUntil = watch(fieldNames.repeatUntil as any) as Date | null
  const watchedTimeZone = fieldNames.timeZone
    ? (watch(fieldNames.timeZone as any) as string | undefined)
    : undefined

  useEffect(() => {
    if (process.env.REACT_APP_ENVIRONMENT === Environment.Production) {
      return
    }

    const timeZoneId = (
      watchedTimeZone ??
      scheduleDatetimeFieldTimeZone ??
      ''
    ).trim()

    const vEvent = scheduleFormToIcal(
      {
        scheduleType: scheduleType as AccessScheduleType,
        temporaryFromDate,
        temporaryToDate,
        scheduleFromDate,
        scheduleToDate,
        repeatUntil,
        frequency: frequency as Frequency,
        daysOfWeek: Array.isArray(daysOfWeekRaw) ? daysOfWeekRaw : [],
        scheduleAllDay,
      },
      { timeZoneId: timeZoneId || null }
    )

    console.log('[ScheduleFields] outgoing vEvent', vEvent)
  }, [
    scheduleType,
    frequency,
    daysOfWeekRaw,
    scheduleAllDay,
    scheduleFromDate,
    scheduleToDate,
    temporaryFromDate,
    temporaryToDate,
    repeatUntil,
    watchedTimeZone,
    scheduleDatetimeFieldTimeZone,
  ])

  const {
    selectedDaysOfWeek,
    firstSelectedDay,
    lastSelectedDay,
    shouldShowScheduleTimeFields,
  } = computeScheduleDayState(daysOfWeekRaw, frequency, valueConfig)

  const resolvedScheduleDatetimeTz = useMemo(
    () =>
      resolveScheduleDatetimeTimezone(
        scheduleDatetimeFieldTimeZone,
        embeddedTimeZoneControl?.currentTimeZone
      ),
    [scheduleDatetimeFieldTimeZone, embeddedTimeZoneControl?.currentTimeZone]
  )

  const formatLabel = (text: string) => text

  const getFieldError = (key: string) =>
    getScheduleFieldsErrorMessage(
      errors as any,
      key,
      shouldShowErrors,
      errorsVisibility
    )

  const daysOfWeekPickerError = () =>
    getScheduleDaysOfWeekPickerError(
      errors as any,
      daysOfWeekErrorField,
      errorsVisibility,
      shouldShowErrors
    )

  const validateInterdependentFields =
    shouldValidateInterdependentScheduleFields(
      shouldShowErrors,
      errorsVisibility
    )

  const initialSnapshot = initialScheduleSnapshot as
    | Record<string, unknown>
    | undefined

  const setVal = (
    name: string,
    value: unknown,
    opts?: typeof defaultSetValueOptions
  ) => {
    setValue(name as any, value as any, { ...defaultSetValueOptions, ...opts })
  }

  const clearStructureErrors = () =>
    clearScheduleStructureErrors({
      fieldNames,
      clearErrors,
      extraFields: extraClearErrorsOnScheduleTypeChange,
    })

  const revalidateAfterStructureChange = () =>
    runScheduleStructureRevalidation({
      shouldValidate: validateInterdependentFields,
      suppressErrors:
        clearScheduleErrorsAfterStructureRevalidate || Boolean(initialSnapshot),
      fieldNames,
      clearStructureErrors: clearStructureErrors,
      trigger,
    })

  const updateScheduleDateValues = (
    opts: ScheduleDateValuesUpdateOptions = {}
  ) => {
    applyScheduleDateValuesUpdate({
      fieldNames,
      valueConfig,
      setVal,
      clearErrors: names => clearErrors(names as any),
      scheduleTimesBeforeAllDayRef,
      scheduleFromDate,
      scheduleToDate,
      frequency,
      scheduleAllDay,
      selectedDaysOfWeek,
      ...opts,
    })
  }

  useEffect(() => {
    if (
      !shouldClearDailyMidnightScheduleTimes({
        disabled,
        scheduleType,
        frequency,
        scheduleAllDay,
        scheduleFromDate,
        scheduleToDate,
        valueConfig,
      })
    ) {
      return
    }

    setVal(fieldNames.scheduleFromDate, null)
    setVal(fieldNames.scheduleToDate, null)
    clearErrors([fieldNames.scheduleFromDate, fieldNames.scheduleToDate] as any)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, scheduleType, frequency, scheduleAllDay])

  const clearDateField = async (fieldName: string) => {
    setVal(fieldName, null)
    if (!validateInterdependentFields) return
    const fields = listTriggerFieldsAfterDateClear(fieldName, fieldNames)
    if (fields) void trigger(fields as any)
    else clearErrors(fieldName as any)
  }

  const handleScheduleTypeChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nextType = event.target.value

    if (resetInactiveScheduleBranchValuesOnTypeChange) {
      resetInactiveScheduleBranchesForType({
        nextType,
        fieldNames,
        valueConfig,
        setVal,
      })
    }

    if (initialSnapshot) {
      applySnapshotForScheduleTypeChange({
        nextScheduleType: nextType,
        initialScheduleSnapshot: initialSnapshot,
        fieldNames,
        setVal,
      })
    } else {
      setValue(fieldNames.scheduleType as any, nextType as any)
    }

    if (nextType === valueConfig.scheduleSchedule && initialSnapshot) {
      const snapFrom = initialSnapshot[fieldNames.scheduleFromDate]
      const snapTo = initialSnapshot[fieldNames.scheduleToDate]
      if (snapFrom != null || snapTo != null) {
        updateScheduleDateValues({
          nextFrequency:
            (initialSnapshot[fieldNames.frequency] as Frequency) ??
            (frequency as Frequency),
          nextDaysOfWeek:
            (initialSnapshot[fieldNames.daysOfWeek] as string[]) ??
            selectedDaysOfWeek,
          nextScheduleAllDay:
            (initialSnapshot[fieldNames.scheduleAllDay] as boolean) ??
            scheduleAllDay,
          nextScheduleFromDate:
            snapFrom != null
              ? (cloneScheduleSnapshotFieldValue(snapFrom) as Date)
              : null,
          nextScheduleToDate:
            snapTo != null
              ? (cloneScheduleSnapshotFieldValue(snapTo) as Date)
              : null,
        })
      }
    }

    clearStructureErrors()
    revalidateAfterStructureChange()
  }

  const handleFrequencyChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    applyScheduleFrequencyChange({
      nextFrequency: event.target.value as Frequency,
      prevFrequency: frequency,
      scheduleType,
      scheduleAllDay,
      selectedDaysOfWeek,
      scheduleFromDate,
      scheduleToDate,
      fieldNames,
      valueConfig,
      clearDaysOfWeekWhenLeavingDaily,
      initialScheduleSnapshot: initialSnapshot,
      defaultWeeklyDays: DEFAULT_WEEKLY_ISO_DAYS,
      defaultSetValueOptions,
      daysOfWeekSetValueOptions,
      setVal,
      clearErrors: (name: string) => clearErrors(name as any),
      extraClearErrorsOnFrequencyChange,
      snapshotForSelectedDays: daysOfWeekSnapshotSelectedRef,
      snapshotForWeekly: daysOfWeekSnapshotWeeklyRef,
      scheduleTimesBeforeWeekPatternClearRef,
      updateScheduleDateValues,
      restoreRecurringFromSnapshot: nextFrequency => {
        if (!initialSnapshot) return
        restoreRecurringFieldsFromSnapshot({
          nextFrequency,
          initialScheduleSnapshot: initialSnapshot,
          fieldNames,
          setVal,
        })
      },
      revalidateAfterStructureChange,
    })
  }

  const handleDayOfWeekChange = async (nextDays: string[]) => {
    setValue(fieldNames.daysOfWeek as any, nextDays as any, {
      ...defaultSetValueOptions,
      ...daysOfWeekSetValueOptions,
    })
    updateScheduleDateValues({ nextDaysOfWeek: nextDays })
    clearErrors(fieldNames.daysOfWeek as any)
    revalidateAfterStructureChange()
  }

  const weeklyRangeSelection = useMemo(
    () => computeWeeklyRangeSelection(selectedDaysOfWeek),
    [selectedDaysOfWeek]
  )

  const isWeeklyFrequency = frequency === valueConfig.freqWeekly

  const scheduleSetTimeCaption = useMemo(() => {
    if (!shouldShowScheduleTimeFields || !isWeeklyFrequency) return null
    const durationMs = computeScheduleDisplayDurationMs({
      scheduleFromDate,
      scheduleToDate,
      scheduleAllDay,
      frequency: frequency as Frequency,
      daysOfWeek: selectedDaysOfWeek,
    })
    return formatScheduleSetTimeLabel(durationMs, labels.setTime, t, {
      alwaysShowLabel: true,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    shouldShowScheduleTimeFields,
    isWeeklyFrequency,
    scheduleFromDate,
    scheduleToDate,
    scheduleAllDay,
    frequency,
    selectedDaysOfWeek,
    t,
  ])

  const showScheduleSetTimeUnderWeekRangeDaySelect =
    isWeeklyFrequency && scheduleAllDay
  const showScheduleSetTimeUnderToTimeField =
    isWeeklyFrequency && !scheduleAllDay

  const handleWeeklyRangeFromChange = (event: SelectChangeEvent<string>) => {
    void handleDayOfWeekChange(
      expandWeekdayRange(event.target.value, weeklyRangeSelection.to)
    )
  }

  const handleWeeklyRangeToChange = (event: SelectChangeEvent<string>) => {
    void handleDayOfWeekChange(
      expandWeekdayRange(weeklyRangeSelection.from, event.target.value)
    )
  }

  const handleScheduleTimeChange = (fieldName: string, value: Date | null) => {
    updateScheduleDateValues({
      nextScheduleFromDate:
        fieldName === fieldNames.scheduleFromDate ? value : scheduleFromDate,
      nextScheduleToDate:
        fieldName === fieldNames.scheduleToDate ? value : scheduleToDate,
    })

    if (validateInterdependentFields) {
      void trigger([
        fieldNames.scheduleFromDate,
        fieldNames.scheduleToDate,
        fieldNames.repeatUntil,
      ] as any)
    }
  }

  const timePickersProps = {
    control,
    fieldNames,
    disabled,
    shouldShow: shouldShowScheduleTimeFields,
    scheduleAllDay,
    fromTimeOpen: scheduleFromTimeOpen,
    toTimeOpen: scheduleToTimeOpen,
    onFromTimeOpen: setScheduleFromTimeOpen,
    onToTimeOpen: setScheduleToTimeOpen,
    firstSelectedDay,
    lastSelectedDay,
    frequency,
    valueConfig,
    t,
    labels,
    formatLabel,
    getFieldError,
    showTimezoneInTimeInputs: datePickerUseShowTimezoneInLabel,
    scheduleTimeZone: resolvedScheduleDatetimeTz,
    minutesStep: scheduleTimePickerMinutesStep,
    onTimeChange: handleScheduleTimeChange,
    toTimeFooterCaption: showScheduleSetTimeUnderToTimeField
      ? scheduleSetTimeCaption
      : null,
  }

  const temporaryPickerShared = buildTemporaryDatePickerSharedProps(
    datePickerUseShowTimezoneInLabel,
    resolvedScheduleDatetimeTz
  )

  const repeatUntilPickerShared = buildRepeatUntilDatePickerSharedProps(
    datePickerUseShowTimezoneInLabel,
    resolvedScheduleDatetimeTz
  )

  const scheduleOptionsProps = {
    control,
    fieldNames,
    valueConfig,
    frequency,
    shouldShowTimeFields: shouldShowScheduleTimeFields,
    scheduleAllDayCheckboxUsesSetValue,
    setScheduleAllDay: (checked: boolean) =>
      setVal(fieldNames.scheduleAllDay, checked),
    onScheduleAllDayChange: ({
      checked,
      wasAllDay,
    }: {
      checked: boolean
      wasAllDay: boolean
    }) => {
      updateScheduleDateValues({
        nextScheduleAllDay: checked,
        fromAllDayToTimed: wasAllDay && !checked,
        previousScheduleAllDay: wasAllDay,
      })
    },
    t,
    labels,
    repeatUntilPickerShared,
    repeatUntilRevalidate,
    shouldValidateInterdependentFields: validateInterdependentFields,
    trigger,
    onClearRepeatUntil: () => clearDateField(fieldNames.repeatUntil),
    clearLabel,
    disabled,
    getFieldError,
  }

  return (
    <Grid item container justifyContent="flex-start">
      <Grid item {...gridItemProps}>
        <RadioGroup
          aria-label="schedule-type"
          id={radioGroupIds.scheduleType ?? 'schedule-type'}
          name={fieldNames.scheduleType}
          value={scheduleType}
          onChange={handleScheduleTypeChange}
        >
          <PermanentAccessSection
            disabled={disabled}
            constantValue={valueConfig.scheduleConstant}
            label={t('accessSchedule.constant')}
          />
          <SectionTimeAccess
            disabled={disabled}
            temporaryValue={valueConfig.scheduleTemporary}
            label={t('accessSchedule.temporary')}
            isActive={scheduleType === valueConfig.scheduleTemporary}
            control={control}
            fieldNames={fieldNames}
            labels={labels}
            clearLabel={clearLabel}
            formatLabel={formatLabel}
            temporaryPickerShared={temporaryPickerShared}
            temporaryDatesRevalidate={temporaryDatesRevalidate}
            shouldValidateInterdependentFields={validateInterdependentFields}
            trigger={trigger}
            onClearDate={clearDateField}
            getFieldError={getFieldError}
            embeddedTimeZoneControl={embeddedTimeZoneControl}
            shouldShowErrors={shouldShowErrors}
            clearErrors={clearErrors}
          />
          <SectionCyclicAccess
            disabled={disabled}
            cyclicValue={valueConfig.scheduleSchedule}
            label={labels.scheduleRadio}
            labels={labels}
            isActive={scheduleType === valueConfig.scheduleSchedule}
            unsupportedRecurringPattern={unsupportedRecurringPattern}
            unsupportedAlertSeverity={unsupportedAlertSeverity}
            embeddedTimeZoneControl={embeddedTimeZoneControl}
            control={control}
            fieldNames={fieldNames}
            valueConfig={valueConfig}
            frequency={frequency}
            t={t}
            radioGroupIds={radioGroupIds}
            onFrequencyChange={handleFrequencyChange}
            selectedDaysOfWeek={selectedDaysOfWeek}
            weeklyRangeFrom={weeklyRangeSelection.from}
            weeklyRangeTo={weeklyRangeSelection.to}
            onWeeklyRangeFromChange={handleWeeklyRangeFromChange}
            onWeeklyRangeToChange={handleWeeklyRangeToChange}
            onSelectedDaysChange={handleDayOfWeekChange}
            daysOfWeekPickerError={daysOfWeekPickerError()}
            shouldShowErrors={shouldShowErrors}
            clearErrors={clearErrors}
            getFieldError={getFieldError}
            timePickersProps={timePickersProps}
            scheduleOptionsProps={scheduleOptionsProps}
            weekRangeToFooterCaption={
              showScheduleSetTimeUnderWeekRangeDaySelect
                ? scheduleSetTimeCaption
                : null
            }
          />
        </RadioGroup>
      </Grid>
    </Grid>
  )
}
